import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { Skill } from 'src/app/domain/skills/types/skills.types';
import { SkillCategories } from 'src/app/domain/skills/types/skills.lookups';
import { MeasurementType, MeasurementTypeDisplayName } from 'src/app/domain/observations/types/observations.lookups';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Dense list-row for a library {@link Skill}.
 *
 * Not FERPA-sensitive — skills are library resources.
 *
 * @feature shared
 */
@Component({
  selector: 'app-skill-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChipComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [disabled]="!clickable()"
      [class.cursor-default]="!clickable()"
      (click)="onClick()"
      [attr.aria-label]="'Open skill ' + skill().name"
    >
      <app-chip [color]="areaColor()" size="xs">{{ skill().area }}</app-chip>

      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-body">{{ skill().name }}</p>
        @if (enabledTypesLabel()) {
          <p class="truncate text-xs text-muted">{{ enabledTypesLabel() }}</p>
        }
      </div>

      @if (clickable()) {
        <app-icon name="chevron-forward" size="xs" color="medium" />
      }
    </button>
  `,
})
export class SkillListItemComponent {
  /** The skill to render. */
  readonly skill = input.required<Skill>();

  /** Whether the row is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the row is clicked. */
  readonly rowClick = output<Skill>();

  /** Map skill category → chip color. Mirrors the four canonical Blume categories. */
  readonly areaColor = computed<'aqua' | 'coral' | 'sand' | 'purple'>(() => {
    switch (this.skill().category) {
      case SkillCategories.ACADEMIC:
        return 'aqua';
      case SkillCategories.BEHAVIOR_SOCIAL_EMOTIONAL:
        return 'coral';
      case SkillCategories.COMMUNICATION:
        return 'sand';
      default:
        return 'purple';
    }
  });

  /** Dot-separated list of measurement-type display names declared on this skill. */
  readonly enabledTypesLabel = computed(() => {
    const s = this.skill();
    const labels: string[] = [];
    for (const type of Object.values(MeasurementType)) {
      if (s.measurements[type]) labels.push(MeasurementTypeDisplayName[type]);
    }
    return labels.join(' · ');
  });

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit(this.skill());
    }
  }
}
